/**
 * The one value-conversion layer. Everything that puts a query result on screen
 * goes through here.
 *
 * Why this file exists: BIGINT, HUGEINT and DECIMAL do not survive a round trip
 * through `number`. The backend serialises them as JSON strings and DuckDB-WASM
 * returns them as JS `bigint`. React throws
 *
 *     Uncaught TypeError: Cannot convert a BigInt value to a number
 *
 * the moment a `bigint` is interpolated into JSX, so no component may ever
 * render a raw cell value. Call `formatCell` (for grid/detail rendering) or
 * `renderableText` (for anything else) instead.
 */

import type { CellValue, ColumnSchema, DuckDbType, TypeClass } from '@/types/api';

const NUMBER_TYPES: ReadonlySet<DuckDbType> = new Set<DuckDbType>([
  'TINYINT',
  'SMALLINT',
  'INTEGER',
  'FLOAT',
  'DOUBLE',
  'UTINYINT',
  'USMALLINT',
  'UINTEGER',
]);

/** Types that arrive as `bigint` or as a decimal string and must never be coerced. */
const WIDE_NUMERIC_TYPES: ReadonlySet<DuckDbType> = new Set<DuckDbType>([
  'BIGINT',
  'HUGEINT',
  'UBIGINT',
  'DECIMAL',
]);

const TEMPORAL_TYPES: ReadonlySet<DuckDbType> = new Set<DuckDbType>([
  'DATE',
  'TIME',
  'TIMESTAMP',
  'TIMESTAMPTZ',
]);

const NESTED_TYPES: ReadonlySet<DuckDbType> = new Set<DuckDbType>([
  'LIST',
  'STRUCT',
  'MAP',
  'JSON',
]);

export function classifyType(type: DuckDbType): TypeClass {
  // Defensive: a column whose `type` is missing must render as text, not crash
  // the grid. One bad field should cost a cell, never the page.
  if (typeof type !== 'string') return 'string';
  if (NUMBER_TYPES.has(type)) return 'number';
  if (WIDE_NUMERIC_TYPES.has(type)) return 'bigint';
  if (type === 'BOOLEAN') return 'boolean';
  if (TEMPORAL_TYPES.has(type)) return 'temporal';
  if (NESTED_TYPES.has(type)) return 'nested';
  if (type === 'BLOB') return 'binary';
  return 'string';
}

/** True for classes that should be right-aligned and are valid for histograms. */
export function isNumericClass(cls: TypeClass): boolean {
  return cls === 'number' || cls === 'bigint';
}

/** Normalises a DuckDB type string such as `DECIMAL(18,4)` to its base type. */
export function baseTypeOf(typeText: string): DuckDbType {
  if (typeof typeText !== 'string' || typeText === '') return 'UNKNOWN';
  const head = typeText.trim().toUpperCase().split(/[\s(<]/, 1)[0] ?? '';
  switch (head) {
    case 'BOOLEAN':
    case 'BOOL':
      return 'BOOLEAN';
    case 'TINYINT':
    case 'INT1':
      return 'TINYINT';
    case 'SMALLINT':
    case 'INT2':
      return 'SMALLINT';
    case 'INTEGER':
    case 'INT':
    case 'INT4':
      return 'INTEGER';
    case 'BIGINT':
    case 'INT8':
      return 'BIGINT';
    case 'HUGEINT':
      return 'HUGEINT';
    case 'UTINYINT':
      return 'UTINYINT';
    case 'USMALLINT':
      return 'USMALLINT';
    case 'UINTEGER':
      return 'UINTEGER';
    case 'UBIGINT':
      return 'UBIGINT';
    case 'FLOAT':
    case 'REAL':
    case 'FLOAT4':
      return 'FLOAT';
    case 'DOUBLE':
    case 'FLOAT8':
      return 'DOUBLE';
    case 'DECIMAL':
    case 'NUMERIC':
      return 'DECIMAL';
    case 'VARCHAR':
    case 'TEXT':
    case 'STRING':
      return 'VARCHAR';
    case 'DATE':
      return 'DATE';
    case 'TIME':
      return 'TIME';
    case 'TIMESTAMP':
    case 'DATETIME':
      return 'TIMESTAMP';
    case 'TIMESTAMPTZ':
      return 'TIMESTAMPTZ';
    case 'INTERVAL':
      return 'INTERVAL';
    case 'UUID':
      return 'UUID';
    case 'BLOB':
    case 'BYTEA':
      return 'BLOB';
    case 'JSON':
      return 'JSON';
    case 'LIST':
      return 'LIST';
    case 'STRUCT':
      return 'STRUCT';
    case 'MAP':
      return 'MAP';
    default:
      // DuckDB renders LIST types as `INTEGER[]`.
      if (typeText.trim().endsWith('[]')) return 'LIST';
      return 'UNKNOWN';
  }
}

/**
 * Coerces one column descriptor off the wire into a usable ColumnSchema.
 *
 * The backend contract is settled, but a shape mismatch on any single field
 * used to take down the whole application (`Cannot read properties of undefined
 * (reading 'toLowerCase')`). Normalising at the boundary means a malformed
 * column renders as an unknown-typed column instead.
 */
export function normalizeColumnSchema(raw: unknown, index: number): ColumnSchema {
  const source = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;

  const name =
    typeof source['name'] === 'string' && source['name'] !== ''
      ? source['name']
      : `column_${index + 1}`;

  const typeText =
    typeof source['typeText'] === 'string' && source['typeText'] !== ''
      ? source['typeText']
      : typeof source['type'] === 'string' && source['type'] !== ''
        ? source['type']
        : 'UNKNOWN';

  const type: DuckDbType =
    typeof source['type'] === 'string' && source['type'] !== ''
      ? baseTypeOf(source['type'])
      : baseTypeOf(typeText);

  return {
    name,
    type,
    typeText,
    nullable: source['nullable'] !== false,
  };
}

export function normalizeColumnSchemas(raw: unknown): ColumnSchema[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry, index) => normalizeColumnSchema(entry, index));
}

/**
 * True only for SQL NULL. Empty string, 0, false and NaN are *values*, and
 * conflating them with NULL is the single most common result-grid bug.
 */
export function isSqlNull(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}

function isTypedArray(value: object): value is Uint8Array {
  return ArrayBuffer.isView(value) && !(value instanceof DataView);
}

/** JSON.stringify replacer that keeps wide integers exact instead of throwing. */
function bigIntSafeReplacer(_key: string, value: unknown): unknown {
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'object' && value !== null && isTypedArray(value)) {
    return Array.from(value);
  }
  return value;
}

/** Parses JSON text back into a value, or returns null when it is not JSON. */
export function tryParseJson(text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

export function stringifyJson(value: unknown, indent = 0): string {
  try {
    return JSON.stringify(value, bigIntSafeReplacer, indent) ?? 'null';
  } catch {
    return String(value);
  }
}

function bytesToHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) {
    out += (bytes[i] ?? 0).toString(16).padStart(2, '0');
  }
  return `\\x${out}`;
}

/**
 * Reinterprets any array view as the bytes it actually occupies.
 *
 * `bytesToHex` pads each element to two hex digits, which is only correct for a
 * byte-width view. Handed a Uint32Array it produced odd-length nonsense --
 * `Uint32Array [313, 0, 0, 0]` rendered as `\x139000000` -- so the width is
 * normalised here rather than assumed by the caller.
 */
function asBytes(view: ArrayBufferView): Uint8Array {
  return view instanceof Uint8Array
    ? view
    : new Uint8Array(view.buffer, view.byteOffset, view.byteLength);
}

/**
 * Decodes a two's-complement little-endian decimal into exact text.
 *
 * Arrow hands back DECIMAL128 as four 32-bit words, and DuckDB exports HUGEINT
 * as DECIMAL128 with scale 0 -- so this is the path every `sum(<integer>)`
 * result takes. There is no 128-bit JS primitive, and Number would silently
 * round above 2^53, so the value is assembled as a BigInt and returned as a
 * string. That matches the server, which already sends wide integers as text
 * (`JsonValueCodec`: `case BigInteger bi -> bi.toString()`).
 */
export function decodeDecimalWords(words: ArrayLike<number>, scale = 0): string {
  if (words.length === 0) return '0';

  let magnitude = 0n;
  for (let i = words.length - 1; i >= 0; i -= 1) {
    // `>>> 0` because a word above 2^31 arrives signed from a non-Uint32 view.
    magnitude = (magnitude << 32n) | BigInt((words[i] ?? 0) >>> 0);
  }

  const bits = BigInt(words.length * 32);
  const signBit = 1n << (bits - 1n);
  let value = magnitude >= signBit ? magnitude - (1n << bits) : magnitude;

  if (scale <= 0) return value.toString();

  const negative = value < 0n;
  if (negative) value = -value;
  // padStart guarantees a leading digit, so 5 at scale 3 is 0.005, not .005.
  const digits = value.toString().padStart(scale + 1, '0');
  const text = `${digits.slice(0, -scale)}.${digits.slice(-scale)}`;
  return negative ? `-${text}` : text;
}

/**
 * Converts any value the engine can return into a string that is safe to place
 * in JSX. Never throws, never returns a non-string, and never loses precision
 * on wide integers.
 *
 * SQL NULL becomes the empty string here -- callers that need to distinguish
 * NULL from an empty string must use `formatCell`, not this.
 */
export function renderableText(value: unknown): string {
  if (isSqlNull(value)) return '';

  switch (typeof value) {
    case 'string':
      return value;
    case 'bigint':
      // The whole reason this module exists.
      return value.toString();
    case 'boolean':
      return value ? 'true' : 'false';
    case 'number':
      if (Number.isNaN(value)) return 'NaN';
      if (!Number.isFinite(value)) return value > 0 ? 'Infinity' : '-Infinity';
      return String(value);
    case 'symbol':
    case 'function':
      return String(value);
    default:
      break;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? 'Invalid Date' : value.toISOString();
  }

  if (typeof value === 'object') {
    if (isTypedArray(value)) return bytesToHex(asBytes(value));

    // Arrays must go to JSON, never through Array.prototype.toString: that
    // renders ['a','b'] as "a,b" and, worse, [] as "" -- which would make an
    // empty array indistinguishable from an empty string on export.
    if (Array.isArray(value)) return stringifyJson(value);

    // Arrow's Decimal128/DecimalBigNum and DuckDB-WASM's wide-int wrappers are
    // plain objects whose only faithful representation is their own toString.
    const proto = Object.getPrototypeOf(value) as object | null;
    const own = (value as { toString?: unknown }).toString;
    const hasCustomToString =
      typeof own === 'function' && own !== Object.prototype.toString;
    const protoToString = proto ? (proto as { toString?: unknown }).toString : undefined;
    const protoHasCustom =
      typeof protoToString === 'function' && protoToString !== Object.prototype.toString;

    if (hasCustomToString || protoHasCustom) {
      try {
        const text = String(value);
        if (text !== '[object Object]') return text;
      } catch {
        /* fall through to JSON */
      }
    }

    return stringifyJson(value);
  }

  return String(value);
}

/**
 * Best-effort conversion to a JS number, for statistics and chart geometry.
 *
 * Returns null rather than NaN when the value is not numeric, so callers must
 * handle the miss. Wide integers go via their string form, which loses
 * precision beyond 2^53 -- acceptable for pixel positions in a sparkline, never
 * acceptable for a displayed value. Display always uses `formatCell`.
 */
export function toFiniteNumber(value: unknown): number | null {
  if (isSqlNull(value)) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'bigint') {
    const asNumber = Number(value);
    return Number.isFinite(asNumber) ? asNumber : null;
  }
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isFinite(time) ? time : null;
  }
  const text = renderableText(value).trim();
  if (text === '') return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Same as `toFiniteNumber` but with a fallback, for counts that must exist. */
export function toCount(value: unknown, fallback = 0): number {
  return toFiniteNumber(value) ?? fallback;
}

export type CellKind = 'null' | 'empty' | 'value';

export interface FormattedCell {
  /** `null` and `empty` must be styled differently -- see .v-null / .v-empty. */
  readonly kind: CellKind;
  /** Always a string. Safe to render in JSX. */
  readonly text: string;
  /** Full untruncated text for tooltips and the detail pane. */
  readonly full: string;
  readonly align: 'left' | 'right';
}

const NULL_GLYPH = 'NULL';
/** Empty string renders as a visible pair of quotes so it cannot read as NULL. */
const EMPTY_GLYPH = '""';

/* ------------------------------------------------------------ temporal --- */

/**
 * Temporal rendering, driven by the column's declared type rather than by the
 * JS type of the value.
 *
 * The same instant reaches us in two different shapes depending on which side
 * answered the query:
 *
 *   server path  -> an ISO-8601 string, "2026-01-01T00:00:00"
 *   cached path  -> an Arrow timestamp, i.e. a number or bigint of epoch units,
 *                   because materialising into DuckDB-WASM and reading back
 *                   loses the original string
 *
 * Rendering off `typeof value` therefore produced "1767225600000" in cached
 * mode and a readable timestamp in server mode. Both shapes are normalised
 * here to one canonical form, so a cached result is indistinguishable from an
 * uncached one -- see the mode-equivalence tests.
 */

const ISO_LIKE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.(\d{1,9}))?)?\s*(Z|[+-]\d{2}:?\d{2})?$/;

const TIME_ONLY = /^(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.(\d{1,9}))?$/;

const MS_PER_DAY = 86_400_000;

function pad(value: number, width = 2): string {
  return String(Math.abs(value)).padStart(width, '0');
}

/**
 * Epoch units vary by Arrow layout (s / ms / µs / ns) and are not carried on the
 * value, so infer from magnitude. For any instant between roughly 1970 and 2100
 * these bands are unambiguous.
 */
function toEpochMillis(raw: number, type: DuckDbType): number | null {
  if (!Number.isFinite(raw)) return null;
  const abs = Math.abs(raw);

  if (type === 'DATE') {
    // Arrow Date32 counts days; Date64 counts milliseconds.
    return abs < 1e6 ? raw * MS_PER_DAY : raw;
  }

  if (type === 'TIME') {
    if (abs <= 86_400) return raw * 1000; // seconds since midnight
    if (abs <= 86_400_000) return raw; // milliseconds
    if (abs <= 86_400_000_000) return raw / 1000; // microseconds
    return raw / 1e6; // nanoseconds
  }

  // No seconds band, deliberately. Seconds and milliseconds are ambiguous for
  // any instant within ~3 years of 1970 (-14182980000 is Apollo 11 in ms, and a
  // date in 2419 in seconds), and Arrow never hands back seconds for a
  // TIMESTAMP -- DuckDB's native unit is microseconds and the WASM bridge
  // yields milliseconds. A backend that really did send epoch seconds would
  // send them as an ISO string anyway.
  if (abs < 1e14) return raw; // milliseconds
  if (abs < 1e17) return raw / 1000; // microseconds
  return raw / 1e6; // nanoseconds
}

function renderFromMillis(ms: number, type: DuckDbType): string | null {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return null;

  const datePart = `${pad(date.getUTCFullYear(), 4)}-${pad(date.getUTCMonth() + 1)}-${pad(
    date.getUTCDate(),
  )}`;

  if (type === 'DATE') return datePart;

  const fraction = date.getUTCMilliseconds();
  const timePart =
    `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}` +
    (fraction === 0 ? '' : `.${pad(fraction, 3)}`);

  if (type === 'TIME') return timePart;
  return `${datePart}T${timePart}`;
}

/** Normalises an already-textual timestamp to the same canonical form. */
function renderFromText(text: string, type: DuckDbType): string | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;

  if (type === 'TIME') {
    const time = TIME_ONLY.exec(trimmed);
    if (!time) return null;
    const fraction = Number((time[4] ?? '').slice(0, 3).padEnd(3, '0'));
    return (
      `${pad(Number(time[1]))}:${pad(Number(time[2]))}:${pad(Number(time[3] ?? '0'))}` +
      (fraction === 0 ? '' : `.${pad(fraction, 3)}`)
    );
  }

  const match = ISO_LIKE.exec(trimmed);
  if (!match) return null;

  const [, year, month, day, hour, minute, second, frac, zone] = match;
  const fraction = Number((frac ?? '').slice(0, 3).padEnd(3, '0'));

  let ms = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour ?? '0'),
    Number(minute ?? '0'),
    Number(second ?? '0'),
    fraction,
  );

  // An explicit non-UTC offset is converted rather than silently dropped.
  if (zone && zone !== 'Z') {
    const sign = zone.startsWith('-') ? -1 : 1;
    const digits = zone.slice(1).replace(':', '');
    const offsetMinutes = Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2, 4));
    ms -= sign * offsetMinutes * 60_000;
  }

  return renderFromMillis(ms, type);
}

/**
 * Canonical text for a temporal value, or null when it cannot be interpreted
 * (in which case the caller falls back to generic rendering).
 */
export function formatTemporal(value: unknown, type: DuckDbType): string | null {
  if (isSqlNull(value)) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : renderFromMillis(value.getTime(), type);
  }

  if (typeof value === 'bigint') {
    const ms = toEpochMillis(Number(value), type);
    return ms === null ? null : renderFromMillis(ms, type);
  }

  if (typeof value === 'number') {
    const ms = toEpochMillis(value, type);
    return ms === null ? null : renderFromMillis(ms, type);
  }

  if (typeof value === 'string') {
    const fromText = renderFromText(value, type);
    if (fromText !== null) return fromText;
    // A bare numeric string is still an epoch value.
    const numeric = Number(value.trim());
    if (value.trim() !== '' && Number.isFinite(numeric)) {
      const ms = toEpochMillis(numeric, type);
      return ms === null ? null : renderFromMillis(ms, type);
    }
  }

  return null;
}

/** Elements shown inline before an array cell collapses to a count. */
const ARRAY_PREVIEW_ITEMS = 3;

/**
 * Compact preview for LIST values.
 *
 * `raqeem.patient_yearly` is 595 columns wide and 559 of those are arrays
 * (DOUBLE[], VARCHAR[], TIMESTAMP[]). Rendering those as raw JSON gives a row
 * of identical truncated brackets, so the grid shows a few elements plus the
 * length -- the length is usually the thing being scanned for. The full value
 * is one click away in the cell detail pane.
 */
export function previewArray(items: readonly unknown[]): string {
  if (items.length === 0) return '[]';

  const shown = items.slice(0, ARRAY_PREVIEW_ITEMS).map((item) => {
    if (isSqlNull(item)) return 'NULL';
    if (typeof item === 'string') return item.length > 16 ? `"${item.slice(0, 16)}…"` : `"${item}"`;
    if (typeof item === 'object') return Array.isArray(item) ? `[…${item.length}]` : '{…}';
    return renderableText(item);
  });

  const rest = items.length - shown.length;
  return rest > 0 ? `[${shown.join(', ')}, …+${rest}]` : `[${shown.join(', ')}]`;
}

/**
 * Non-finite DOUBLEs. JSON has no literal for these, so the backend sends them
 * as the strings "NaN" / "Infinity" / "-Infinity". They must stay right-aligned
 * with the rest of the column and must not be mistaken for text.
 */
const NON_FINITE = new Set(['NaN', 'Infinity', '-Infinity', 'inf', '-inf', '-Inf', 'Inf']);

export function isNonFiniteNumeric(value: unknown): boolean {
  if (typeof value === 'number') return !Number.isFinite(value);
  return typeof value === 'string' && NON_FINITE.has(value);
}

/** Bytes behind a base64 BLOB, for the "N bytes" hint. Returns null if invalid. */
export function base64ByteLength(text: string): number | null {
  if (text === '') return 0;
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(text) || text.length % 4 !== 0) return null;
  const padding = text.endsWith('==') ? 2 : text.endsWith('=') ? 1 : 0;
  return (text.length / 4) * 3 - padding;
}

export interface FormatOptions {
  /** Group thousands in fixed-width integer types. Off inside the detail pane. */
  readonly groupDigits?: boolean;
  /** Trim long values for the grid. 0 disables. */
  readonly maxLength?: number;
}

/**
 * U+202F NARROW NO-BREAK SPACE, the thousands separator in grouped numbers.
 *
 * A comma is ambiguous against locales that use it as a decimal mark, and a
 * plain space lets a number wrap mid-value inside a narrow grid cell. Exported
 * so tests assert against the constant rather than an invisible literal.
 */
export const DIGIT_SEPARATOR = ' ';

const DIGIT_GROUP = /\B(?=(\d{3})+(?!\d))/g;

function groupInteger(text: string): string {
  const negative = text.startsWith('-');
  const body = negative ? text.slice(1) : text;
  if (!/^\d+$/.test(body)) return text;
  return (negative ? '-' : '') + body.replace(DIGIT_GROUP, DIGIT_SEPARATOR);
}

/**
 * Formats one cell for display, preserving the NULL / empty-string distinction.
 */
export function formatCell(
  value: CellValue,
  type: DuckDbType,
  options: FormatOptions = {},
): FormattedCell {
  const cls = classifyType(type);
  const align = isNumericClass(cls) ? 'right' : 'left';

  if (isSqlNull(value)) {
    return { kind: 'null', text: NULL_GLYPH, full: NULL_GLYPH, align };
  }

  // Temporal columns are rendered from the declared type, so the server path
  // (ISO string) and the cached path (Arrow epoch number) agree exactly.
  if (cls === 'temporal') {
    const canonical = formatTemporal(value, type);
    if (canonical !== null) {
      return { kind: 'value', text: canonical, full: canonical, align };
    }
  }

  let full = renderableText(value);
  let preview: string | null = null;

  if (cls === 'nested') {
    // The server sends a real array/object; the cache stores nested types as
    // JSON text and hands back a string. Re-parse so both modes render the
    // same thing.
    const nested = typeof value === 'string' ? tryParseJson(value) : value;

    if (typeof nested === 'object' && nested !== null) {
      full = stringifyJson(nested);
      // Arrays get a length-bearing preview; the full JSON stays available for
      // the tooltip, the detail pane and copy actions.
      if (Array.isArray(nested)) preview = previewArray(nested);
    }
  }

  if (full === '' && (cls === 'string' || cls === 'nested')) {
    return { kind: 'empty', text: EMPTY_GLYPH, full: '', align };
  }

  if (preview !== null) {
    return { kind: 'value', text: preview, full, align };
  }

  // BLOBs arrive base64-encoded. The raw base64 is what a user would copy, so
  // keep it as the full value and lead the cell with the decoded size.
  if (cls === 'binary' && typeof value === 'string') {
    const bytes = base64ByteLength(value);
    const label = bytes === null ? full : `${bytes} B · ${full}`;
    const max = options.maxLength ?? 0;
    return {
      kind: 'value',
      text: max > 0 && label.length > max ? `${label.slice(0, max)}…` : label,
      full,
      align,
    };
  }

  // "NaN" / "Infinity" arrive as strings; grouping would be nonsense and the
  // value must not be dressed up as anything other than what it is.
  if (isNonFiniteNumeric(value)) {
    return { kind: 'value', text: full, full, align };
  }

  if (options.groupDigits && isNumericClass(cls) && !full.includes('e')) {
    const dot = full.indexOf('.');
    full = dot === -1 ? groupInteger(full) : groupInteger(full.slice(0, dot)) + full.slice(dot);
  }

  const max = options.maxLength ?? 0;
  const text = max > 0 && full.length > max ? `${full.slice(0, max)}…` : full;

  return { kind: 'value', text, full, align };
}

/**
 * Renders a value as a SQL literal, for "copy as filter" actions in the column
 * explorer. Wide integers stay exact; strings are single-quote escaped.
 */
export function toSqlLiteral(value: CellValue, type: DuckDbType): string {
  if (isSqlNull(value)) return 'NULL';

  const cls = classifyType(type);
  if (cls === 'boolean') return value ? 'TRUE' : 'FALSE';

  if (isNumericClass(cls)) {
    const text = renderableText(value);
    return /^-?\d+(\.\d+)?([eE][-+]?\d+)?$/.test(text) ? text : `'${text.replace(/'/g, "''")}'`;
  }

  // Copy-as-SQL from a cached cell must produce a usable literal, not an epoch.
  const text = cls === 'temporal' ? (formatTemporal(value, type) ?? renderableText(value)) : renderableText(value);
  const quoted = `'${text.replace(/'/g, "''")}'`;

  switch (cls) {
    case 'temporal':
      return `${type} ${quoted}`;
    case 'nested':
      return `${quoted}::JSON`;
    default:
      return quoted;
  }
}

/** Quotes an identifier for generated SQL. */
export function quoteIdent(name: string): string {
  return `"${name.replace(/"/g, '""')}"`;
}
